import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { EidasUser } from '../_model/eidasUser';
import { OotsRequest } from '../_model/oots-request';
import { CookieService } from 'ngx-cookie-service';
import { OOTSRequestStatus } from '../_model/oots-request-status';

@Injectable({
  providedIn: 'root'
})
export class StateService {

  constructor(private cookieService: CookieService) {
  }

  public respondSede = false;

  private currentUserNameStore = new BehaviorSubject<string>('');
  private currentUserStore: BehaviorSubject<EidasUser> = new BehaviorSubject<EidasUser>(
    {
      dateOfBirth: new Date(2000, 1, 1),
      familyName: '',
      firstName: '',
      personIdentifier: '',
      schemeId: '',
      loa: ''
    });

  public currentUserName = this.currentUserNameStore.asObservable();
  public currentUser = this.currentUserStore.asObservable();

  private ootsRequestSubject: BehaviorSubject<OotsRequest> = new BehaviorSubject<OotsRequest>(new OotsRequest());
  public ootsRequestObserver = this.ootsRequestSubject.asObservable();

  private ootsPreviewSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public ootsPreviewObserver = this.ootsPreviewSubject.asObservable();

  private agreementSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public agreementObserver = this.agreementSubject.asObservable();

  private actionSubject: BehaviorSubject<string> = new BehaviorSubject<string>("");
  public actionObserver = this.actionSubject.asObservable();

  private requirementStatusSubject: BehaviorSubject<string> = new BehaviorSubject<string>("");
  public requirementStatusObserver = this.requirementStatusSubject.asObservable();

  private continuationTokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>("");
  public continuationTokenObserver = this.continuationTokenSubject.asObservable();

  private language: BehaviorSubject<string> = new BehaviorSubject<string>("es");
  getLanguage = this.language.asObservable();

  private ootsRequestModelSubject: BehaviorSubject<string> = new BehaviorSubject<string>("");
  public ootsRequestModelObserver = this.ootsRequestModelSubject.asObservable();

  private viewPdfMapSubject: BehaviorSubject<Map<string, boolean>> = new BehaviorSubject<Map<string, boolean>>(new Map);
  public viewPdfMapObserver = this.viewPdfMapSubject.asObservable();

  private loadingStateSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public loadingStateObserver = this.loadingStateSubject.asObservable();

  public validities: string[] = [];

  setLanguage(language: string) {
    this.language.next(language)
  }

  set loadingState(isLoading: boolean) {
    this.loadingStateSubject.next(isLoading);
  }

  set action(action: string) {
    this.actionSubject.next(action);
  }

  set ootsRequest(ootsRequest: OotsRequest) {
    this.ootsRequestSubject.next(ootsRequest);
  }

  get ootsRequest() {
    return this.ootsRequestSubject.getValue();
  }

  set ootsPreview(ootsPreview: boolean) {
    this.ootsPreviewSubject.next(ootsPreview);
  }

  get ootsPreview() {
    return this.ootsPreviewSubject.getValue();
  }

  set agreementAccepted(accepted: boolean) {
    this.agreementSubject.next(accepted);
  }

  setCurrentUserName(userName: string): void {
    this.currentUserNameStore.next(userName);
  }

  setCurrentUser(currentUser: EidasUser): void {
    this.currentUserStore.next(currentUser);
  }

  public getCurrentUser(): EidasUser {
    return this.currentUserStore.getValue();
  }

  set continuationToken(token: string) {
    this.cookieService.set("continuationToken", token, { expires: new Date(new Date().getTime() + 1000 * 60 * 60), path: '/' });
  }

  get continuationToken(): string {
    return this.cookieService.get("continuationToken");
  }

  public clearContinuationToken() {
    this.cookieService.delete("continuationToken", '/');
  }

  public retrieveRequestStatus(): OOTSRequestStatus | null {
    const cookieValue = this.cookieService.get("oots-request-status");
    if (cookieValue) {
      try {
        return JSON.parse(cookieValue) as OOTSRequestStatus;
      } catch (error) {
        console.error("Error parsing cookie value:", error);
        return null;
      }
    }
    return null;
  }

  public saveRequestStatus(evidenceStatusMap: OOTSRequestStatus): void {
    const evidenceStatus = JSON.stringify(evidenceStatusMap);
    this.cookieService.set("oots-request-status", evidenceStatus, { expires: new Date(new Date().getTime() + 1000 * 60 * 60), path: '/' });
  }

  public clearRequestStatus(): void {
    this.cookieService.delete("oots-request-status", '/');
  }

  public setPreview(isPreview: string) {
    this.cookieService.set("isPreview", isPreview, { path: '/' });
  }

  public isPreview(): string {
    if (this.cookieService.get("isPreview") !== '') return JSON.parse(this.cookieService.get("isPreview"));
    else return "true";
  }

  public requirementStatusChanged() {
    this.requirementStatusSubject.next('');
  }

  public ootsRequestModelChanged(uuid: string) {
    this.ootsRequestModelSubject.next(uuid);
  }

  public viewPdfMapInitialize(requirementIdList: string[]) {
    const newViewPdfMap = new Map<string, boolean>();
    requirementIdList.forEach(requirementId => {
      newViewPdfMap.set(requirementId, false);
    })
    this.viewPdfMapSubject.next(newViewPdfMap);
  }

  public viewPdfMapChange(requirementId: string, status: boolean) {
    const viewPdfMap = this.viewPdfMapSubject.getValue();
    const newViewPdfMap = new Map<string, boolean>();
    viewPdfMap.forEach((value: any, key: any) => {
      if (requirementId != undefined && key == requirementId) {
        newViewPdfMap.set(key, status);
      }
      else {
        newViewPdfMap.set(key, false);
      }

    })
    this.viewPdfMapSubject.next(newViewPdfMap);
  }

  public getLoadingState(): boolean {
    return this.loadingStateSubject.value;
  }
}
